/* /app/immobilier — module Immobilier
 * Portefeuille (CRUD actifs), Valorisation (vs prix médian commune),
 * Promotion (matrice de faisabilité promoteur), lecture IA NOAM + PDF.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import AiText from "@/components/AiText";
import { useTranslation } from "react-i18next";
import {
  Plus, Trash2, Loader2, Building2, Sparkles, FileText, Edit3, Save, X,
  TrendingUp, MapPin, Hammer, BarChart3, Info, Globe, Search, ExternalLink, RefreshCw, AlertTriangle, Radio, FileCheck,
} from "lucide-react";
import PermitsTab from "./immobilier/PermitsTab";
import DocumentAttachZone from "@/components/DocumentAttachZone";
import MarketSizeWidget from "@/components/MarketSizeWidget";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const PROPERTY_TYPES = ["residentiel", "commercial", "mixte", "terrain", "rendement"];
const PHASES = ["acquisition", "detention", "cession"];

const fmtChf = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  return Math.round(Number(n)).toLocaleString("fr-CH").replace(/,/g, "'") + " CHF";
};

export default function Immobilier() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("portfolio");

  return (
    <div className="space-y-6" data-testid="immobilier-root">
      <ThreeQPageHeader endpoint="/workspace/immobilier/top-priority" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[#378ADD] text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5">
            <Building2 className="w-3 h-3" /> {t("immo.overline", { defaultValue: "Tactique" })}
          </div>
          <h1 className="noam-display text-3xl font-bold mt-1">
            {t("immo.title", { defaultValue: "Immobilier" })}
          </h1>
          <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
            {t("immo.subtitle", { defaultValue: "Portefeuille immobilier, valorisation comparée et matrice de faisabilité promoteur." })}
          </p>
        </div>
        <ModuleHelpButton moduleKey="immobilier" />
      </div>

      <MarketSizeWidget moduleKey="immobilier" title={t("immo.title", { defaultValue: "Immobilier" })} />

      <div className="flex gap-1 border-b border-white/5">
        {[
          { id: "portfolio", label: t("immo.tab_portfolio", { defaultValue: "Portefeuille" }), icon: Building2 },
          { id: "valuation", label: t("immo.tab_valuation", { defaultValue: "Valorisation" }), icon: TrendingUp },
          { id: "feasibility", label: t("immo.tab_feasibility", { defaultValue: "Promotion" }), icon: Hammer },
          { id: "public_sources", label: t("immo.tab_public_sources", { defaultValue: "Sources publiques" }), icon: Globe },
          { id: "permits", label: t("immo.tab_permits", { defaultValue: "Permis suisses" }), icon: FileCheck },
        ].map((tt) => {
          const Icon = tt.icon;
          const active = tab === tt.id;
          return (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              data-testid={`immo-tab-${tt.id}`}
              className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                active ? "border-[#378ADD] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {tt.label}
            </button>
          );
        })}
      </div>

      {tab === "portfolio" && <PortfolioTab />}
      {tab === "valuation" && <ValuationTab />}
      {tab === "feasibility" && <FeasibilityTab />}
      {tab === "public_sources" && <PublicSourcesTab />}
      {tab === "permits" && <PermitsTab />}
    </div>
  );
}

/* =========================== Portefeuille ============================ */

function PortfolioTab() {
  const { t } = useTranslation();
  const [data, setData] = useState({ items: [], kpi: {}, is_empty: true });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/workspace/realestate/portfolio"); setData(r.data); }
    catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t("immo.confirm_delete", { defaultValue: "Supprimer cet actif ?" }))) return;
    try { await api.delete(`/workspace/realestate/portfolio/${id}`); notify.success("Supprimé"); load(); }
    catch (e) { notify.error(fmtError(e)); }
  };

  if (loading) return <Loading />;

  const k = data.kpi || {};

  return (
    <div className="space-y-5" data-testid="immo-portfolio">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label={t("immo.kpi_count", { defaultValue: "Actifs" })} value={k.count ?? 0} />
        <Kpi label={t("immo.kpi_total", { defaultValue: "Valeur totale" })} value={fmtChf(k.total_valeur_chf)} />
        <Kpi label={t("immo.kpi_locatif", { defaultValue: "Locatif mensuel" })} value={fmtChf(k.total_locatif_mensuel_chf)} />
        <Kpi label={t("immo.kpi_ltv", { defaultValue: "LTV moyen" })} value={`${k.ltv_moyen_pct ?? 0} %`} />
        <Kpi label={t("immo.kpi_rendement", { defaultValue: "Rendement brut" })} value={`${k.rendement_brut_pct ?? 0} %`} />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">{(data.items || []).length} {t("immo.assets_count", { defaultValue: "actif(s)" })}</div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="immo-new-asset-btn">
          <Plus className="w-3.5 h-3.5" /> {t("immo.new_asset", { defaultValue: "Nouvel actif" })}
        </button>
      </div>

      {data.is_empty ? (
        <EmptyState
          icon={Building2}
          title={t("immo.empty_assets_title", { defaultValue: "Portefeuille vide" })}
          desc={t("immo.empty_assets_desc", { defaultValue: "Ajoutez un actif pour démarrer." })}
          cta={t("immo.new_asset", { defaultValue: "Nouvel actif" })}
          onCta={() => setCreating(true)}
          testid="immo-empty-portfolio"
        />
      ) : (
        <div className="noam-card overflow-hidden">
          <table className="w-full text-sm" data-testid="immo-assets-table">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-white/5">
              <tr>
                <th className="text-left p-3">{t("immo.col_label", { defaultValue: "Actif" })}</th>
                <th className="text-left p-3">{t("immo.col_location", { defaultValue: "Lieu" })}</th>
                <th className="text-left p-3">{t("immo.col_type", { defaultValue: "Type" })}</th>
                <th className="text-right p-3">{t("immo.col_surface", { defaultValue: "Surface" })}</th>
                <th className="text-right p-3">{t("immo.col_value", { defaultValue: "Valeur" })}</th>
                <th className="text-right p-3">{t("immo.col_ltv", { defaultValue: "LTV" })}</th>
                <th className="text-left p-3">{t("immo.col_phase", { defaultValue: "Phase" })}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((a) => (
                <tr key={a.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`immo-asset-row-${a.id}`}>
                  <td className="p-3 font-medium">{a.label}</td>
                  <td className="p-3 text-zinc-400 text-xs">
                    <MapPin className="w-3 h-3 inline mr-1 opacity-60" />
                    {[a.commune, a.canton].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="p-3 text-xs">
                    <span className="px-1.5 py-0.5 rounded bg-white/5 text-zinc-300">{a.type}</span>
                  </td>
                  <td className="p-3 text-right text-zinc-400">{a.surface_m2 || 0} m²</td>
                  <td className="p-3 text-right">{fmtChf(a.valeur_venale_chf)}</td>
                  <td className="p-3 text-right text-zinc-400">{a.ltv_pct || 0} %</td>
                  <td className="p-3 text-xs text-zinc-400">{a.phase}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing(a)} className="text-zinc-400 hover:text-white p-1" data-testid={`immo-edit-${a.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(a.id)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`immo-del-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <AssetModal
          asset={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AssetModal({ asset, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!asset;
  const [form, setForm] = useState({
    label: asset?.label || "",
    address: asset?.address || "",
    commune: asset?.commune || "",
    canton: asset?.canton || "",
    type: asset?.type || "residentiel",
    surface_m2: asset?.surface_m2 || 0,
    valeur_venale_chf: asset?.valeur_venale_chf || 0,
    valeur_locative_chf: asset?.valeur_locative_chf || 0,
    ltv_pct: asset?.ltv_pct || 0,
    garantie: asset?.garantie || "",
    phase: asset?.phase || "detention",
    notes: asset?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/workspace/realestate/portfolio/${asset.id}`, form);
      else await api.post("/workspace/realestate/portfolio", form);
      notify.success(t("common.saved", { defaultValue: "Enregistré." }));
      onSaved();
    } catch (e) { notify.error(fmtError(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="immo-asset-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="noam-card p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="noam-display text-lg font-semibold">
            {isEdit ? t("immo.edit_asset", { defaultValue: "Modifier l'actif" }) : t("immo.new_asset", { defaultValue: "Nouvel actif" })}
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder={t("immo.field_label", { defaultValue: "Désignation" })} className="noam-input" data-testid="immo-form-label" />
        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
          placeholder={t("immo.field_address", { defaultValue: "Adresse" })} className="noam-input" data-testid="immo-form-address" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })}
            placeholder={t("immo.field_commune", { defaultValue: "Commune" })} className="noam-input" data-testid="immo-form-commune" />
          <input value={form.canton} onChange={(e) => setForm({ ...form, canton: e.target.value })}
            placeholder={t("immo.field_canton", { defaultValue: "Canton" })} className="noam-input" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="noam-input" data-testid="immo-form-type">
            {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} className="noam-input">
            {PHASES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumField label={t("immo.field_surface", { defaultValue: "Surface (m²)" })} value={form.surface_m2} onChange={(v) => setForm({ ...form, surface_m2: v })} testid="immo-form-surface" />
          <NumField label={t("immo.field_value", { defaultValue: "Valeur vénale (CHF)" })} value={form.valeur_venale_chf} onChange={(v) => setForm({ ...form, valeur_venale_chf: v })} testid="immo-form-value" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumField label={t("immo.field_locatif", { defaultValue: "Loyer mensuel (CHF)" })} value={form.valeur_locative_chf} onChange={(v) => setForm({ ...form, valeur_locative_chf: v })} />
          <NumField label={t("immo.field_ltv", { defaultValue: "LTV (%)" })} value={form.ltv_pct} onChange={(v) => setForm({ ...form, ltv_pct: v })} />
        </div>
        <input value={form.garantie} onChange={(e) => setForm({ ...form, garantie: e.target.value })}
          placeholder={t("immo.field_garantie", { defaultValue: "Garantie / banque" })} className="noam-input" />
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
          placeholder={t("immo.field_notes", { defaultValue: "Notes" })} className="noam-input resize-none" />
        {isEdit && asset?.id && (
          <DocumentAttachZone module="immobilier" entityId={asset.id} entityLabel={asset?.label} compact />
        )}
        <button type="submit" disabled={busy || !form.label.trim()} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="immo-form-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {isEdit ? t("common.save", { defaultValue: "Enregistrer" }) : t("common.create", { defaultValue: "Créer" })}
        </button>
      </form>
    </div>
  );
}

/* =========================== Valorisation ============================ */

function ValuationTab() {
  const { t } = useTranslation();
  const [val, setVal] = useState({ items: [], ref_count: 0 });
  const [refs, setRefs] = useState([]);
  const [providers, setProviders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refModalOpen, setRefModalOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiText, setAiText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [v, r, p] = await Promise.all([
        api.get("/workspace/realestate/valuation"),
        api.get("/workspace/realestate/market-refs"),
        api.get("/workspace/realestate/providers/status"),
      ]);
      setVal(v.data); setRefs(r.data.items || []); setProviders(p.data);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const runAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/workspace/realestate/valuation/ai-analysis");
      setAiText(r.data.ai_reading);
      notify.success(t("immo.ai_done", { defaultValue: "Analyse IA prête." }));
    } catch (e) { notify.error(fmtError(e)); }
    setAiBusy(false);
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-5" data-testid="immo-valuation">
      {providers && (
        <div className="noam-card p-4 flex items-start gap-3" data-testid="immo-providers-status">
          <Info className="w-4 h-4 text-[#378ADD] mt-0.5 flex-shrink-0" />
          <div className="text-xs text-zinc-400 flex-1">
            <strong className="text-zinc-200">
              {t("immo.providers_mode", { defaultValue: "Mode actif" })} : {providers.active_mode}
            </strong>
            <div className="mt-1 text-[11px]">
              {(providers.providers || []).map((p) => (
                <span key={p.name} className={`inline-block mr-3 ${p.configured ? "text-[#1D9E75]" : "text-zinc-600"}`}>
                  {p.configured ? "●" : "○"} {p.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">
          {val.items.length} {t("immo.assets_count", { defaultValue: "actif(s)" })} · {val.ref_count} {t("immo.refs_count", { defaultValue: "réf. marché" })}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setRefModalOpen(true)} className="btn-secondary text-xs" data-testid="immo-add-ref-btn">
            <Plus className="w-3 h-3" /> {t("immo.add_ref", { defaultValue: "Saisir prix médian commune" })}
          </button>
          <button onClick={runAI} disabled={aiBusy || val.items.length === 0} className="btn-primary text-xs disabled:opacity-50" data-testid="immo-val-ai-btn">
            {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {t("immo.ai_btn", { defaultValue: "Lecture IA" })}
          </button>
        </div>
      </div>

      {val.items.length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title={t("immo.empty_val_title", { defaultValue: "Aucun actif à valoriser" })}
          desc={t("immo.empty_val_desc", { defaultValue: "Ajoutez d'abord des actifs et des prix médians par commune." })}
          testid="immo-empty-valuation"
        />
      ) : (
        <div className="noam-card overflow-hidden">
          <table className="w-full text-sm" data-testid="immo-valuation-table">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-white/5">
              <tr>
                <th className="text-left p-3">{t("immo.col_label", { defaultValue: "Actif" })}</th>
                <th className="text-left p-3">{t("immo.col_commune", { defaultValue: "Commune" })}</th>
                <th className="text-right p-3">m²</th>
                <th className="text-right p-3">{t("immo.col_decl_m2", { defaultValue: "CHF/m² décl." })}</th>
                <th className="text-right p-3">{t("immo.col_market_m2", { defaultValue: "CHF/m² marché" })}</th>
                <th className="text-right p-3">{t("immo.col_market_total", { defaultValue: "Marché estimé" })}</th>
                <th className="text-right p-3">{t("immo.col_ecart", { defaultValue: "Écart" })}</th>
                <th className="text-left p-3">{t("immo.col_badge", { defaultValue: "Verdict" })}</th>
              </tr>
            </thead>
            <tbody>
              {val.items.map((it) => (
                <tr key={it.asset_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`immo-val-row-${it.asset_id}`}>
                  <td className="p-3 font-medium">{it.label}</td>
                  <td className="p-3 text-xs text-zinc-400">{it.commune || "—"}</td>
                  <td className="p-3 text-right text-zinc-400">{it.surface_m2}</td>
                  <td className="p-3 text-right">{it.prix_m2_declare_chf ? fmtChf(it.prix_m2_declare_chf) : "—"}</td>
                  <td className="p-3 text-right">{it.prix_m2_marche_chf ? fmtChf(it.prix_m2_marche_chf) : <span className="text-zinc-600 text-xs">pas de réf.</span>}</td>
                  <td className="p-3 text-right">{it.valeur_marche_estimee_chf ? fmtChf(it.valeur_marche_estimee_chf) : "—"}</td>
                  <td className="p-3 text-right">{it.ecart_pct !== null && it.ecart_pct !== undefined ? `${it.ecart_pct > 0 ? "+" : ""}${it.ecart_pct} %` : "—"}</td>
                  <td className="p-3"><Badge badge={it.badge} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aiText && (
        <div className="noam-card p-5" data-testid="immo-val-ai-reading">
          <div className="text-[#378ADD] text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {t("immo.ai_reading", { defaultValue: "Lecture IA" })}
          </div>
          <pre className="whitespace-pre-wrap text-sm text-zinc-200 mt-2 font-sans"><AiText>{aiText}</AiText></pre>
        </div>
      )}

      {refs.length > 0 && (
        <div className="noam-card p-5">
          <div className="noam-display text-base font-semibold mb-2">{t("immo.refs_title", { defaultValue: "Références marché saisies" })}</div>
          <div className="grid md:grid-cols-2 gap-2 text-xs">
            {refs.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-2 rounded bg-white/5">
                <span className="text-zinc-300">{r.commune} <span className="text-zinc-500">· {r.type}</span></span>
                <span className="text-[#378ADD] font-semibold">{fmtChf(r.prix_median_m2_chf)}/m²</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {refModalOpen && <MarketRefModal onClose={() => setRefModalOpen(false)} onSaved={() => { setRefModalOpen(false); load(); }} />}
    </div>
  );
}

function Badge({ badge }) {
  if (!badge) return <span className="text-zinc-600 text-xs">—</span>;
  const map = {
    sous_evalue: { c: "#1D9E75", l: "Sous-évalué" },
    juste_valeur: { c: "#378ADD", l: "Juste valeur" },
    sur_evalue: { c: "#E24B4A", l: "Sur-évalué" },
  };
  const m = map[badge] || { c: "#888", l: badge };
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded font-bold whitespace-nowrap"
      style={{ background: `${m.c}22`, color: m.c, border: `1px solid ${m.c}55` }}>
      {m.l}
    </span>
  );
}

function MarketRefModal({ onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ commune: "", canton: "", type: "residentiel", prix_median_m2_chf: 0, source: "saisie_manuelle" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/workspace/realestate/market-refs", form);
      notify.success("Référence enregistrée");
      onSaved();
    } catch (e) { notify.error(fmtError(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="immo-ref-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="noam-card p-6 w-full max-w-md space-y-3">
        <div className="flex items-center justify-between">
          <div className="noam-display text-lg font-semibold">{t("immo.add_ref", { defaultValue: "Prix médian par commune" })}</div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input required value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })}
          placeholder={t("immo.field_commune", { defaultValue: "Commune" })} className="noam-input" data-testid="immo-ref-commune" />
        <input value={form.canton} onChange={(e) => setForm({ ...form, canton: e.target.value })}
          placeholder={t("immo.field_canton", { defaultValue: "Canton" })} className="noam-input" />
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="noam-input">
          {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <NumField label={t("immo.field_price_m2", { defaultValue: "Prix médian (CHF/m²)" })} value={form.prix_median_m2_chf} onChange={(v) => setForm({ ...form, prix_median_m2_chf: v })} testid="immo-ref-price" />
        <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
          placeholder={t("immo.field_source", { defaultValue: "Source (OFS, gérance, …)" })} className="noam-input" />
        <button type="submit" disabled={busy || !form.commune.trim() || !form.prix_median_m2_chf} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="immo-ref-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {t("common.save", { defaultValue: "Enregistrer" })}
        </button>
      </form>
    </div>
  );
}

/* =========================== Promotion =============================== */

function FeasibilityTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/workspace/realestate/feasibility"); setItems(r.data.items || []); }
    catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t("immo.confirm_delete_feas", { defaultValue: "Supprimer cette faisabilité ?" }))) return;
    try { await api.delete(`/workspace/realestate/feasibility/${id}`); notify.success("Supprimée"); load(); }
    catch (e) { notify.error(fmtError(e)); }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-5" data-testid="immo-feasibility">
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">{items.length} {t("immo.feas_count", { defaultValue: "matrice(s)" })}</div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="immo-new-feas-btn">
          <Plus className="w-3.5 h-3.5" /> {t("immo.new_feas", { defaultValue: "Nouvelle matrice promoteur" })}
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={Hammer}
          title={t("immo.empty_feas_title", { defaultValue: "Aucune matrice" })}
          desc={t("immo.empty_feas_desc", { defaultValue: "Calculez la marge nette d'un projet promoteur en quelques clics." })}
          cta={t("immo.new_feas", { defaultValue: "Nouvelle matrice promoteur" })}
          onCta={() => setCreating(true)}
          testid="immo-empty-feas"
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((it) => (
            <FeasibilityCard key={it.id} item={it} onEdit={() => setEditing(it)} onRemove={() => remove(it.id)} onReload={load} />
          ))}
        </div>
      )}

      {(creating || editing) && (
        <FeasibilityModal item={editing} onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}

function FeasibilityCard({ item, onEdit, onRemove, onReload }) {
  const { t } = useTranslation();
  const [aiBusy, setAiBusy] = useState(false);
  const c = item.computed || {};
  const verdictColor = { viable: "#1D9E75", limite: "#EF9F27", non_viable: "#E24B4A" }[c.verdict] || "#888";

  const runAI = async () => {
    setAiBusy(true);
    try { await api.post(`/workspace/realestate/feasibility/${item.id}/ai-analysis`); onReload(); }
    catch (e) { notify.error(fmtError(e)); }
    setAiBusy(false);
  };
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/workspace/realestate/feasibility/${item.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `faisabilite-${item.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (e) { notify.error(fmtError(e)); }
  };

  return (
    <div className="noam-card p-5" data-testid={`immo-feas-card-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="noam-display text-base font-semibold flex-1 truncate">{item.label}</div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="text-zinc-400 hover:text-white p-1"><Edit3 className="w-3.5 h-3.5" /></button>
          <button onClick={onRemove} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <Line k={t("immo.f_revenu", { defaultValue: "Revenu vente" })} v={fmtChf(c.revenu_vente_chf)} />
        <Line k={t("immo.f_cout", { defaultValue: "Coût total" })} v={fmtChf(c.cout_total_chf)} />
        <Line k={t("immo.f_loyer_argent", { defaultValue: "Loyer argent" })} v={fmtChf(c.loyer_argent_chf)} />
        <Line k={t("immo.f_marge", { defaultValue: "Marge nette" })} v={fmtChf(c.marge_nette_chf)} highlight />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-3xl font-bold" style={{ color: verdictColor }} data-testid={`immo-feas-margin-${item.id}`}>
          {c.marge_pct}%
        </div>
        <span className="text-[10px] px-2 py-1 rounded font-bold uppercase"
          style={{ background: `${verdictColor}22`, color: verdictColor }}>
          {c.verdict}
        </span>
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={runAI} disabled={aiBusy} className="btn-primary text-xs flex-1 justify-center disabled:opacity-50" data-testid={`immo-feas-ai-${item.id}`}>
          {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {t("immo.ai_btn", { defaultValue: "Lecture IA" })}
        </button>
        <button onClick={downloadPdf} className="btn-secondary text-xs" data-testid={`immo-feas-pdf-${item.id}`}>
          <FileText className="w-3 h-3" /> PDF
        </button>
      </div>
      {item.ai_reading && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <pre className="whitespace-pre-wrap text-xs text-zinc-300 font-sans"><AiText>{item.ai_reading}</AiText></pre>
        </div>
      )}
    </div>
  );
}

function Line({ k, v, highlight }) {
  return (
    <div className={`flex justify-between ${highlight ? "font-semibold" : ""}`}>
      <span className="text-zinc-500">{k}</span>
      <span className={highlight ? "text-white" : "text-zinc-300"}>{v}</span>
    </div>
  );
}

function FeasibilityModal({ item, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!item;
  const [form, setForm] = useState({
    label: item?.label || "",
    prix_terrain_chf: item?.prix_terrain_chf || 0,
    acompte_pct: item?.acompte_pct || 10,
    surface_sbp_m2: item?.surface_sbp_m2 || 0,
    cout_construction_chf_m2: item?.cout_construction_chf_m2 || 0,
    prix_vente_chf_m2: item?.prix_vente_chf_m2 || 0,
    duree_mois: item?.duree_mois || 24,
    taux_argent_pct: item?.taux_argent_pct || 4,
    frais_annexes_pct: item?.frais_annexes_pct || 8,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/workspace/realestate/feasibility/${item.id}`, form);
      else await api.post("/workspace/realestate/feasibility", form);
      notify.success(t("common.saved", { defaultValue: "Enregistré." }));
      onSaved();
    } catch (e) { notify.error(fmtError(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="immo-feas-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="noam-card p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="noam-display text-lg font-semibold">
            {isEdit ? t("immo.edit_feas", { defaultValue: "Modifier matrice" }) : t("immo.new_feas", { defaultValue: "Nouvelle matrice promoteur" })}
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder={t("immo.f_label", { defaultValue: "Nom du projet" })} className="noam-input" data-testid="immo-feas-label" />
        <div className="grid grid-cols-2 gap-2">
          <NumField label={t("immo.f_prix_terrain", { defaultValue: "Prix terrain (CHF)" })} value={form.prix_terrain_chf} onChange={(v) => setForm({ ...form, prix_terrain_chf: v })} testid="immo-feas-terrain" />
          <NumField label={t("immo.f_acompte", { defaultValue: "Acompte (%)" })} value={form.acompte_pct} onChange={(v) => setForm({ ...form, acompte_pct: v })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumField label={t("immo.f_surface_sbp", { defaultValue: "SBP (m²)" })} value={form.surface_sbp_m2} onChange={(v) => setForm({ ...form, surface_sbp_m2: v })} testid="immo-feas-sbp" />
          <NumField label={t("immo.f_cout_constr", { defaultValue: "Coût construction (CHF/m²)" })} value={form.cout_construction_chf_m2} onChange={(v) => setForm({ ...form, cout_construction_chf_m2: v })} testid="immo-feas-constr" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumField label={t("immo.f_prix_vente", { defaultValue: "Prix vente cible (CHF/m²)" })} value={form.prix_vente_chf_m2} onChange={(v) => setForm({ ...form, prix_vente_chf_m2: v })} testid="immo-feas-vente" />
          <NumField label={t("immo.f_duree", { defaultValue: "Durée (mois)" })} value={form.duree_mois} onChange={(v) => setForm({ ...form, duree_mois: v })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumField label={t("immo.f_taux", { defaultValue: "Taux argent (%)" })} value={form.taux_argent_pct} onChange={(v) => setForm({ ...form, taux_argent_pct: v })} />
          <NumField label={t("immo.f_frais", { defaultValue: "Frais annexes (%)" })} value={form.frais_annexes_pct} onChange={(v) => setForm({ ...form, frais_annexes_pct: v })} />
        </div>
        <button type="submit" disabled={busy || !form.label.trim()} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="immo-feas-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {isEdit ? t("common.save", { defaultValue: "Enregistrer" }) : t("common.create", { defaultValue: "Créer" })}
        </button>
      </form>
    </div>
  );
}

/* =========================== Shared bits ============================= */

function Kpi({ label, value }) {
  return (
    <div className="noam-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-lg font-bold mt-1">{value}</div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <input type="number" step="any" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="noam-input w-full" data-testid={testid} />
    </label>
  );
}

function EmptyState({ icon: Icon, title, desc, cta, onCta, testid }) {
  return (
    <div className="noam-card p-8 text-center" data-testid={testid}>
      <Icon className="w-8 h-8 text-[#378ADD] mx-auto opacity-60" />
      <div className="noam-display text-lg mt-3">{title}</div>
      <p className="text-zinc-400 text-sm mt-1 max-w-md mx-auto">{desc}</p>
      {cta && onCta && (
        <button onClick={onCta} className="btn-primary mt-4">
          <Plus className="w-3.5 h-3.5" /> {cta}
        </button>
      )}
    </div>
  );
}

function Loading() {
  const { t } = useTranslation();
  return (
    <div className="text-zinc-400 text-sm flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}
    </div>
  );
}

/* =========================== Sources publiques suisses ============================ */

function PublicSourcesTab() {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("address");
  const [canton, setCanton] = useState("");
  const [deep, setDeep] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [enrichingId, setEnrichingId] = useState(null);
  // P2 — Veille passive immobilière
  const [watchState, setWatchState] = useState(null);
  const [watchingId, setWatchingId] = useState(null);
  const [runningAll, setRunningAll] = useState(false);

  const loadWatch = async () => {
    try {
      const r = await api.get("/workspace/realestate/public-watch/state");
      setWatchState(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    api.get("/workspace/realestate/public-sources/info")
      .then((r) => setInfo(r.data))
      .catch(() => {});
    api.get("/workspace/realestate/portfolio")
      .then((r) => setAssets(r.data?.items || []))
      .catch(() => {});
    loadWatch();
  }, []);

  const toggleWatch = async (assetId, enabled) => {
    try {
      await api.put(`/workspace/realestate/portfolio/${assetId}/public-watch`, { enabled });
      await loadWatch();
      notify.success(enabled ? "Veille activée." : "Veille désactivée.");
    } catch (e) {
      notify.error(fmtError(e, "Toggle veille impossible."));
    }
  };

  const runWatchAsset = async (assetId) => {
    setWatchingId(assetId);
    try {
      const r = await api.post(`/workspace/realestate/portfolio/${assetId}/public-watch/run`);
      if (r.data?.checked) {
        notify.success(`Check OK : ${r.data?.signals_created || 0} nouveau(x) signal(aux) sur ${r.data?.sources_total || 0} sources.`);
        await loadWatch();
      } else {
        notify.error(`Skip : ${r.data?.skipped_reason || "raison inconnue"}.`);
      }
    } catch (e) {
      notify.error(fmtError(e, "Check impossible."));
    } finally { setWatchingId(null); }
  };

  const runWatchAll = async () => {
    setRunningAll(true);
    try {
      const r = await api.post("/workspace/realestate/public-watch/run-all");
      notify.success(`Veille globale : ${r.data?.signals_created || 0} signal(aux) sur ${r.data?.checked || 0}/${r.data?.assets_total || 0} actif(s) checké(s).`);
      await loadWatch();
    } catch (e) {
      notify.error(fmtError(e, "Veille globale impossible."));
    } finally { setRunningAll(false); }
  };

  const runSearch = async () => {
    if (!query.trim()) {
      notify.error("Saisis une adresse, un n° de parcelle ou des mots-clés.");
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const r = await api.post("/workspace/realestate/public-sources/search", {
        query: query.trim(),
        kind,
        canton: canton || null,
        deep,
      });
      setResults(r.data);
      if (r.data?.error) notify.error(r.data.error);
      else notify.success(`Recherche : ${r.data?.sources?.length || 0} source(s) publique(s) trouvée(s).`);
    } catch (e) {
      notify.error(fmtError(e, "Recherche impossible."));
    } finally { setLoading(false); }
  };

  const enrichAsset = async (assetId) => {
    setEnrichingId(assetId);
    try {
      const r = await api.post(`/workspace/realestate/portfolio/${assetId}/enrich-public`);
      const a = assets.find((x) => x.id === assetId);
      notify.success(`Enrichissement OK pour ${a?.label || "actif"} (${r.data?.enrichment?.sources?.length || 0} sources).`);
      // reload assets to get the new enrichment
      const fresh = await api.get("/workspace/realestate/portfolio");
      setAssets(fresh.data?.items || []);
    } catch (e) {
      notify.error(fmtError(e, "Enrichissement impossible."));
    } finally { setEnrichingId(null); }
  };

  // P2 — Auto-extraction NOAM depuis raw_pages
  const [extractingId, setExtractingId] = useState(null);
  const [extractProposal, setExtractProposal] = useState(null); // { assetId, proposal }
  const extractAsset = async (assetId) => {
    setExtractingId(assetId);
    try {
      const r = await api.post(`/workspace/realestate/portfolio/${assetId}/extract-public`);
      setExtractProposal({ assetId, proposal: r.data?.proposal });
    } catch (e) {
      notify.error(fmtError(e, "Extraction impossible."));
    } finally { setExtractingId(null); }
  };
  const applyExtraction = async () => {
    if (!extractProposal) return;
    try {
      const r = await api.post(`/workspace/realestate/portfolio/${extractProposal.assetId}/extract-public/apply`);
      notify.success(`Appliqué : ${(r.data?.applied_fields || []).length} champ(s).`);
      setExtractProposal(null);
      const fresh = await api.get("/workspace/realestate/portfolio");
      setAssets(fresh.data?.items || []);
    } catch (e) {
      notify.error(fmtError(e, "Application impossible."));
    }
  };

  return (
    <div className="space-y-5" data-testid="immo-public-sources">
      <div className="noam-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Globe className="w-3.5 h-3.5 text-[#1D9E75]" />
          <div className="noam-overline">{t("immo.public_sources.title", { defaultValue: "Sources publiques suisses" })}</div>
        </div>
        <p className="text-[11px] text-zinc-400 leading-relaxed">
          {t("immo.public_sources.help", { defaultValue:
            "Interrogez en temps réel les sources publiques officielles (geo.admin.ch, SITG, RegBL, États cantonaux). Tavily filtre la recherche sur les domaines autorisés. Disponible en FR / EN / DE."
          })}
        </p>

        {!info?.tavily_configured && (
          <div className="flex items-start gap-2 border border-[#EF9F27]/30 bg-[#EF9F27]/5 rounded-md p-2">
            <AlertTriangle className="w-3.5 h-3.5 text-[#EF9F27] flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-zinc-300">
              <strong className="text-[#EF9F27]">TAVILY_API_KEY non configurée.</strong> La recherche temps réel est désactivée.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[120px_120px_1fr_auto_auto] gap-2 items-end">
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500">{t("immo.public_sources.kind", { defaultValue: "Type" })}</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="noam-input w-full text-xs mt-1"
              data-testid="immo-pub-kind"
            >
              <option value="address">{t("immo.public_sources.kind_address", { defaultValue: "Adresse" })}</option>
              <option value="parcel">{t("immo.public_sources.kind_parcel", { defaultValue: "Parcelle" })}</option>
              <option value="permit">{t("immo.public_sources.kind_permit", { defaultValue: "Permis" })}</option>
              <option value="free">{t("immo.public_sources.kind_free", { defaultValue: "Libre" })}</option>
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500">{t("immo.public_sources.canton", { defaultValue: "Canton" })}</label>
            <select
              value={canton}
              onChange={(e) => setCanton(e.target.value)}
              className="noam-input w-full text-xs mt-1"
              data-testid="immo-pub-canton"
            >
              <option value="">{t("immo.public_sources.canton_all", { defaultValue: "Tous" })}</option>
              <option value="Genève">Genève</option>
              <option value="Vaud">Vaud</option>
              <option value="Zurich">Zurich</option>
              <option value="Bâle">Bâle</option>
              <option value="Berne">Berne</option>
              <option value="Valais">Valais</option>
              <option value="Fribourg">Fribourg</option>
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500">{t("immo.public_sources.query", { defaultValue: "Requête" })}</label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder={t("immo.public_sources.query_placeholder", { defaultValue: "ex: Rue du Mont-Blanc 17, Genève" })}
              className="noam-input w-full text-xs mt-1"
              data-testid="immo-pub-query"
            />
          </div>
          <label className="inline-flex items-center gap-1 text-[10px] text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} data-testid="immo-pub-deep" />
            Deep
          </label>
          <button
            type="button"
            onClick={runSearch}
            disabled={loading || !info?.tavily_configured}
            className="btn-primary text-xs disabled:opacity-50"
            data-testid="immo-pub-search-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            {loading ? t("common.loading", { defaultValue: "..." }) : t("immo.public_sources.search_btn", { defaultValue: "Rechercher" })}
          </button>
        </div>

        {/* Domains list */}
        {info?.sources?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {info.sources.map((s) => (
              <span
                key={s.domain}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] border border-white/10 bg-white/[0.02] text-zinc-400"
                title={`${s.scope} — ${s.data}`}
              >
                {s.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="noam-card p-4 space-y-3" data-testid="immo-pub-results">
          {results.answer && (
            <div className="border border-[#378ADD]/30 rounded-md p-3 bg-[#378ADD]/5">
              <div className="noam-overline text-[#378ADD] mb-1">{t("immo.public_sources.synthesis", { defaultValue: "Synthèse" })}</div>
              <p className="text-sm text-zinc-100 leading-relaxed">{results.answer}</p>
            </div>
          )}
          <div className="space-y-2">
            <div className="noam-overline text-zinc-400">{t("immo.public_sources.results", { defaultValue: "Sources" })} ({results.sources?.length || 0})</div>
            {(results.sources || []).length === 0 ? (
              <p className="text-[11px] text-zinc-500 italic">{t("immo.public_sources.no_results", { defaultValue: "Aucun résultat sur les sources autorisées." })}</p>
            ) : (
              <ul className="space-y-2">
                {results.sources.map((s, i) => (
                  <li key={i} className="border border-white/5 rounded-md p-2 hover:bg-white/[0.02] transition-colors">
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-zinc-100 hover:text-[#378ADD] inline-flex items-center gap-1.5">
                      {s.title} <ExternalLink className="w-3 h-3" />
                    </a>
                    {s.snippet && <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed line-clamp-2">{s.snippet}</p>}
                    <div className="text-[9px] text-zinc-600 mt-1 truncate">{s.url}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* P2 — Sentinelle territoriale */}
      <div className="noam-card p-4 space-y-3" data-testid="immo-watch-state">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-[#1D9E75] animate-pulse" />
            <div className="noam-overline">{t("immo.watch.title", { defaultValue: "Sentinelle territoriale (veille passive)" })}</div>
          </div>
          <button
            type="button"
            onClick={runWatchAll}
            disabled={runningAll || !info?.tavily_configured}
            className="btn-ghost text-xs disabled:opacity-50"
            data-testid="immo-watch-run-all"
          >
            {runningAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {t("immo.watch.run_all", { defaultValue: "Vérifier maintenant (toutes)" })}
          </button>
        </div>
        <p className="text-[11px] text-zinc-400 leading-relaxed">
          {t("immo.watch.help", { defaultValue:
            "Noam surveille automatiquement chaque actif sur les sources publiques whitelistées (cadence hebdomadaire). Tout nouveau permis, mutation ou changement d'affectation détecté devient un signal Radar (scope: immobilier_local) + alerte."
          })}
        </p>
        {watchState && (
          <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
            <div className="border border-white/10 rounded-md py-2 bg-white/[0.02]">
              <div className="text-zinc-500 uppercase tracking-wider">{t("immo.watch.kpi_total", { defaultValue: "Actifs total" })}</div>
              <div className="text-xl font-bold text-zinc-100 mt-0.5">{watchState.assets_total}</div>
            </div>
            <div className="border border-[#1D9E75]/30 rounded-md py-2 bg-[#1D9E75]/5">
              <div className="text-zinc-500 uppercase tracking-wider">{t("immo.watch.kpi_watched", { defaultValue: "Surveillés" })}</div>
              <div className="text-xl font-bold text-[#1D9E75] mt-0.5">{watchState.watched}</div>
            </div>
            <div className="border border-[#EF9F27]/30 rounded-md py-2 bg-[#EF9F27]/5">
              <div className="text-zinc-500 uppercase tracking-wider">{t("immo.watch.kpi_signals_30d", { defaultValue: "Signaux 30j" })}</div>
              <div className="text-xl font-bold text-[#EF9F27] mt-0.5">{watchState.signals_30d}</div>
            </div>
          </div>
        )}
        {watchState?.items?.length > 0 && (
          <ul className="space-y-1.5">
            {watchState.items.map((it) => (
              <li
                key={it.id}
                className="flex items-center justify-between gap-2 border border-white/5 rounded-md p-2"
                data-testid={`immo-watch-asset-${it.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-100 truncate inline-flex items-center gap-1.5">
                    {it.enabled ? <Radio className="w-3 h-3 text-[#1D9E75]" /> : <Radio className="w-3 h-3 text-zinc-600" />}
                    {it.label || "—"}
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    {it.address || t("immo.watch.no_address", { defaultValue: "Sans adresse" })}{it.canton ? ` · ${it.canton}` : ""}
                    {it.last_check && <span className="ml-2">· {t("immo.watch.last_check", { defaultValue: "Dernier check" })} {it.last_check.slice(0, 10)}</span>}
                    {it.last_signals_count > 0 && <span className="ml-2 text-[#EF9F27]">· {it.last_signals_count} {t("immo.watch.signal_word", { defaultValue: "signal", count: it.last_signals_count })}{it.last_signals_count > 1 ? "s" : ""}</span>}
                  </div>
                </div>
                <label className="inline-flex items-center gap-1 text-[10px] text-zinc-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!it.enabled}
                    onChange={(e) => toggleWatch(it.id, e.target.checked)}
                    data-testid={`immo-watch-toggle-${it.id}`}
                  />
                  {t("immo.watch.toggle_label", { defaultValue: "Veille" })}
                </label>
                <button
                  type="button"
                  onClick={() => runWatchAsset(it.id)}
                  disabled={watchingId === it.id || !info?.tavily_configured || (!it.address && !it.canton)}
                  className="btn-ghost text-xs disabled:opacity-50"
                  data-testid={`immo-watch-run-${it.id}`}
                  title={t("immo.watch.run_one_title", { defaultValue: "Lancer un check immédiat" })}
                >
                  {watchingId === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Enrich existing assets */}
      <div className="noam-card p-4 space-y-3" data-testid="immo-pub-enrich">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#EF9F27]" />
          <div className="noam-overline">{t("immo.public_sources.enrich_title", { defaultValue: "Enrichir un actif du portefeuille" })}</div>
        </div>
        <p className="text-[11px] text-zinc-400">
          {t("immo.public_sources.enrich_help", { defaultValue:
            "Lance une recherche ciblée sur les sources publiques à partir de l'adresse de l'actif. Les résultats sont attachés à l'actif pour consultation future."
          })}
        </p>
        {assets.length === 0 ? (
          <p className="text-[11px] text-zinc-500 italic">Aucun actif dans le portefeuille.</p>
        ) : (
          <ul className="space-y-1.5">
            {assets.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 border border-white/5 rounded-md p-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-100 truncate">{a.label}</div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    {a.address || "—"}{a.commune ? ` · ${a.commune}` : ""}{a.canton ? ` · ${a.canton}` : ""}
                    {a.public_sources_enrichment && <span className="text-[#1D9E75] ml-2">· enrichi le {a.public_sources_enrichment.fetched_at?.slice(0, 10)}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => enrichAsset(a.id)}
                  disabled={enrichingId === a.id || !info?.tavily_configured || (!a.address && !a.commune)}
                  className="btn-ghost text-xs disabled:opacity-50"
                  data-testid={`immo-pub-enrich-${a.id}`}
                >
                  {enrichingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {t("immo.public_sources.enrich_btn", { defaultValue: "Enrichir" })}
                </button>
                {a.public_sources_enrichment && (
                  <button
                    type="button"
                    onClick={() => extractAsset(a.id)}
                    disabled={extractingId === a.id}
                    className="btn-ghost text-xs disabled:opacity-50"
                    data-testid={`immo-pub-extract-${a.id}`}
                    title="NOAM extrait zone d'affectation, valeur fiscale, etc. depuis les pages publiques"
                  >
                    {extractingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Extraire
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* P2 — Modal proposition d'extraction NOAM */}
      {extractProposal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setExtractProposal(null)}
          data-testid="immo-extract-modal"
        >
          <div className="noam-card w-full max-w-2xl my-8 p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#EF9F27]" />
                <h2 className="noam-display text-base font-bold">Proposition d'extraction NOAM</h2>
              </div>
              <button onClick={() => setExtractProposal(null)} className="text-zinc-500 hover:text-zinc-200" data-testid="immo-extract-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[11px] text-zinc-400">
              Confiance NOAM : <strong className="text-zinc-200">{extractProposal.proposal?.confidence_pct ?? "?"}%</strong>
            </div>
            <dl className="space-y-2 text-xs">
              {extractProposal.proposal?.zone_affectation && (
                <div><dt className="text-zinc-500 text-[10px] uppercase">Zone d'affectation</dt><dd className="text-zinc-100">{extractProposal.proposal.zone_affectation}</dd></div>
              )}
              {extractProposal.proposal?.valeur_fiscale_chf && (
                <div><dt className="text-zinc-500 text-[10px] uppercase">Valeur fiscale</dt><dd className="text-zinc-100">{Number(extractProposal.proposal.valeur_fiscale_chf).toLocaleString("fr-CH")} CHF</dd></div>
              )}
              {extractProposal.proposal?.surface_m2_regbl && (
                <div><dt className="text-zinc-500 text-[10px] uppercase">Surface RegBL</dt><dd className="text-zinc-100">{extractProposal.proposal.surface_m2_regbl} m²</dd></div>
              )}
              {extractProposal.proposal?.annee_construction && (
                <div><dt className="text-zinc-500 text-[10px] uppercase">Année construction</dt><dd className="text-zinc-100">{extractProposal.proposal.annee_construction}</dd></div>
              )}
              {extractProposal.proposal?.restrictions && (
                <div><dt className="text-zinc-500 text-[10px] uppercase">Restrictions</dt><dd className="text-zinc-100">{extractProposal.proposal.restrictions}</dd></div>
              )}
              {extractProposal.proposal?.permis_recents?.length > 0 && (
                <div><dt className="text-zinc-500 text-[10px] uppercase">Permis récents</dt><dd className="text-zinc-100">
                  <ul className="list-disc list-inside">
                    {extractProposal.proposal.permis_recents.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </dd></div>
              )}
            </dl>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <button onClick={() => setExtractProposal(null)} className="btn-ghost text-xs" data-testid="immo-extract-cancel">Annuler</button>
              <button onClick={applyExtraction} className="btn-primary text-xs" data-testid="immo-extract-apply">
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


